import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js';
import TWEEN from '@tweenjs/tween.js'
import * as MATH from './mymath.js';
import { Colors } from './colors.js';
import { MeshLine, MeshLineMaterial } from 'three.meshline';


class WeightVector {
    constructor (x, y, z, color=0x00ff00, label) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.color = color;
        const end = new THREE.Vector3( x, y, z );
        const dir = end.normalize();
        const origin = new THREE.Vector3( 0, 0, 0 );
        const length = this.length();
        this.head_length = .35;
        this.head_width = .2;
        this.arrow = new THREE.ArrowHelper( dir, origin, length, color, this.head_length, this.head_width);
        this.raycaster = new THREE.Raycaster();

        const text = document.createElement( 'div' );
        text.className = 'w-label';
        const rep = this.rep_obj();
        text.style.color = '#' + rep.material.color.getHexString();
        text.textContent = label || 'w';

        this.label = new CSS2DObject( text );
        rep.getWorldPosition(this.label.position);

        this.group = new THREE.Group()
        this.group.add( this.arrow );
        this.group.add( this.label );
    }

    length () {
        return Math.sqrt(this.x ** 2 + this.y ** 2 + this.z ** 2);
    }

    add ( other ) {
        const x = this.x + other.x;
        const y = this.y + other.y;
        const z = this.z + other.z;
        const this_color = new THREE.Color(this.color);
        const other_color = new THREE.Color(other.color);
        const comb_color = this_color.lerp(other_color, .5);
        const color = comb_color.getHex();
        return new WeightVector(x, y, z, color, this.label);
    }

    scale (value) {
        const x = this.x * value;
        const y = this.y * value;
        const z = this.z * value;
        return new WeightVector(x, y, z, this.color, this.label);
    }

    add_to (scene) {
        scene.add(this.group);
    }

    rep_obj () {
        return this.arrow.children[1];
    }

    move_to (pos) {
        let dir = new THREE.Vector3( pos.x, pos.y, pos.z);
        this.x = pos.x;
        this.y = pos.y;
        this.z = pos.z;
        dir = dir.normalize();
        this.arrow.setDirection(dir);
        this.arrow.setLength(this.length(), this.head_length, this.head_width);
        // Update location of label
        this.rep_obj().getWorldPosition(this.label.position);
    }

    pick(mouse, scene, camera) {
        let object = null;
        this.raycaster.setFromCamera(mouse, camera);

        const intersectedObjects = this.raycaster.intersectObjects([this.rep_obj()]);
        if (intersectedObjects.length) {
            let candidate = intersectedObjects[0].object;
            if ( candidate.uuid === this.rep_obj().uuid ) {
                object = this;
            }
        }
        return object;
    }
}


class Polytope {

    rotateX(radians, animationTime=500) {
        if ( animationTime > 0 ) {
            let angle = {angle: 0};
            let prevAngle = angle.angle;
            const endAngle = {angle: radians};
            let tween = new TWEEN.Tween(angle).to(endAngle, animationTime).start();
            tween.onUpdate( function() {
                this.polytope.rotateX(angle.angle - prevAngle);
                this.vertices.rotateX(angle.angle - prevAngle);
                prevAngle = angle.angle;
            }.bind(this));
        }
        else {
            this.polytope.rotateX(radians);
            this.vertices.rotateX(radians);
        }
    }

    rotateY(radians, animationTime=500) {
        if ( animationTime > 0 ) {
            let angle = {angle: 0};
            let prevAngle = angle.angle;
            const endAngle = {angle: radians};
            let tween = new TWEEN.Tween(angle).to(endAngle, animationTime).start();
            tween.onUpdate( function() {
                this.polytope.rotateY(angle.angle - prevAngle);
                this.vertices.rotateY(angle.angle - prevAngle);
                prevAngle = angle.angle;
            }.bind(this));
        }
        else {
            this.polytope.rotateY(radians);
            this.vertices.rotateY(radians);
        }
    }

    rotateZ(radians, animationTime=500) {
        if ( animationTime > 0 ) {
            let angle = {angle: 0};
            let prevAngle = angle.angle;
            const endAngle = {angle: radians};
            let tween = new TWEEN.Tween(angle).to(endAngle, animationTime).start();
            tween.onUpdate( function() {
                this.polytope.rotateZ(angle.angle - prevAngle);
                this.vertices.rotateZ(angle.angle - prevAngle);
                prevAngle = angle.angle;
            }.bind(this));
        }
        else {
            this.polytope.rotateZ(radians);
            this.vertices.rotateZ(radians);
        }
    }
}


class Permutohedron extends Polytope {
    constructor (position, dim, scale, colors, opacity, cayley) {
        super();
        this.position = position;
        this.dim = dim;
        this.scale = scale ?? 1.;
        this.opacity = opacity ?? .1;
        this.colors = colors ?? 0x555555;
        this.cayley = cayley ?? true;
        this.permutations = MATH.permutations(dim);

        if ( this.dim == 4 ) {
			this.vertexScale = Math.sqrt(5);
            const projection = [[1., 1., 0., 0.],
                                [1., 0., 1., 0.],
                                [1., 0., 0., 1.]];
            this.positions = [];
            for ( let p of this.permutations ) {
                let row = [];
                for ( let i = 0; i < projection.length; i++ ) {
                    row.push((MATH.dotprod(p, projection[i]) - 3));
                }
                this.positions.push(row);
            }
            this.faces = [ 3, 1, 0,
                           1, 3, 2,
                           2, 3, 5,
                           5, 3, 4,
                           2, 5, 6,
                           2, 6, 9,
                           2, 9, 1,
                           9, 14, 1,
                           14, 13, 1,
                           13, 10, 1,
                           0, 1, 10,
                           10, 11, 0,
                           11, 10, 13,
                           11, 13, 22,
                           11, 22, 23,
                           11, 23, 12,
                           13, 14, 21,
                           13, 21, 22,
                           7, 16, 17,
                           7, 17, 6,
                           7, 6, 5,
                           7, 5, 4,
                           6, 17, 18,
                           6, 18, 21,
                           6, 21, 14,
                           6, 14, 9,
                           16, 19, 18,
                           16, 18, 17,
                           19, 20, 23,
                           19, 23, 22,
                           19, 22, 21,
                           19, 21, 18,
                           4, 3, 8,
                           4, 8, 7,
                           7, 8, 15,
                           7, 15, 20,
                           7, 20, 19,
                           7, 19, 16,
                           15, 12, 23,
                           15, 23, 20,
                           8, 3, 0,
                           8, 0, 11,
                           8, 11, 12,
                           8, 12, 15
                           ];
        }
        else if ( dim == 3 ) {
			this.vertexScale = Math.sqrt(2);
            this.positions = [];
            for ( let p of this.permutations ) {
                let row = [];
                for ( let i = 0; i < dim; i++ ) {
                    row.push(p[i] - 1);
                }
                this.positions.push(row);
            }
            this.faces = [ 0, 1, 5,
                           0, 5, 4,
                           0, 4, 3,
                           1, 2, 5,
            ];
        }
		else {
			this.vertexScale = Math.sqrt(2);
            this.positions = [[ -1.,  0.,  1.],
                              [ 1.,  0.,  -1.]];
            this.faces = [];
		}

        this.vertexPositions = [];
        for ( const v of this.positions ) {
            const [x, y, z] = v;
            let point = new THREE.Vector3(x * this.scale + this.position[0],
                                          y * this.scale + this.position[1],
                                          z * this.scale + this.position[2]);
            this.vertexPositions.push(point);
        }
        this.vertices = this.constructVertices();
        this.polytope = this.constructPolytope();
    }

    color_vertex( v, color, visible=true ) {
        const tcolor = new THREE.Color(color);
        v.material.color = tcolor;
        let label = this.vertexLabel[v.uuid];
        label.element.style.color = '#' + tcolor.getHexString();
        // v.visible = visible;
        label.visible = visible;
    }

    color_vertices( color, opacity, visible=true ) {
        for ( let i = 0; i < this.vertices.children.length; i++ ) {
            this.color_vertex(this.vertices.children[i], color, opacity);
        }
    }

    constructVertices() {
        this.labels = [];
        let vertices = new THREE.Group();
        let points = new THREE.Group();
        let labels = new THREE.Group();
        const geometry = new THREE.SphereGeometry(.04, 16, 16);
        const material = new THREE.MeshLambertMaterial( { transparent: true, opacity: 0.9 } );
        for (let i = 0; i < this.positions.length; i++) {

            let sphere = new THREE.Mesh(geometry, material);

            sphere.position.copy( this.vertexPositions[i]);

            // Allow each vertex to be colored independently
            sphere.material = sphere.material.clone();

            let perm = this.permutations[i];
            if ( this.cayley ) {
                perm = MATH.argsort(perm);
            }

            if ( Array.isArray(this.colors) ) {
                sphere.material.color = new THREE.Color(this.colors[perm[0]]);
            }
            else {
                sphere.material.color = new THREE.Color(this.colors);
            }

            points.add( sphere );

            const text = document.createElement( 'div' );
            text.className = 'label';
            for ( let p of perm ) {
                const cl = document.createElement( 'span' );
                cl.style.color = '#' + new THREE.Color(this.colors[p]).getHexString();
                if ( this.cayley ) {
                    // cl.textContent = String.fromCharCode(97 + p);
                    cl.textContent = p;
                }
                else {
                    cl.textContent = p;
                }
                text.appendChild(cl);
            }
            text.style.color = '#' + sphere.material.color.getHexString();
            this.labels.push(text);

            const label = new CSS2DObject( text );
            let pos = sphere.position.clone();
            label.position.copy( pos );
            labels.add( label );
        }
        vertices.add( points );
        vertices.add( labels );
        return vertices;
    }

    constructPolytope() {

        let positions = [].concat(...this.positions);
        const geometry = new THREE.PolyhedronBufferGeometry( positions, this.faces, this.vertexScale * this.scale, 0 );
        const material = new THREE.MeshNormalMaterial({flatShading: true, transparent: true, opacity: this.opacity});
        // const material = new THREE.MeshPhysicalMaterial({ transparent: true, opacity: 0.5, reflectivity: 0., side: BackSide});
        let obj = new THREE.Mesh(geometry, material);

        const [x, y, z] = this.position;
        obj.translateX(x);
        obj.translateY(y);
        obj.translateZ(z);

        return obj;
    }

}


class Hyperplane {
    constructor (x, y, z, offset, color, width=5, height=5, opacity=.5) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.offset = offset;
        this.color = color;
        this.object = new THREE.Group();
        this.material = new THREE.MeshPhysicalMaterial( {color: color, side: THREE.DoubleSide, transparent:true, opacity: opacity} );
        this.intersections = [];
        this.geometry = new THREE.PlaneGeometry(width, height);
        this.mesh = new THREE.Mesh(this.geometry, this.material);
        this.dir = new THREE.Vector3(x, y, z).normalize();
        this.mesh.updateMatrixWorld();
        this.mesh.lookAt(this.dir);
        this.mesh.position.set(-this.dir.x * this.offset,
                               -this.dir.y * this.offset,
                               -this.dir.z * this.offset);
        this.plane = new THREE.Plane(this.dir, this.offset);
        this.object.add(this.mesh);
    }

    lookAt (dir) {
        this.dir = dir;
        this.dir.normalize();
        this.mesh.position.set(0, 0, 0);
        this.mesh.lookAt(this.dir);
        this.mesh.position.set(-this.dir.x * this.offset,
                               -this.dir.y * this.offset,
                               -this.dir.z * this.offset);
        this.plane.set(this.dir, this.offset);
    }

    translate (offset) {
        this.offset = offset;
        this.mesh.position.set(-this.dir.x * offset, -this.dir.y * offset, -this.dir.z * offset);
    }

    pointA() {
        this.mesh.updateWorldMatrix();
        let point = new THREE.Vector3(...this.geometry.attributes.position.array.slice(0, 3));
        return this.mesh.localToWorld(point);
    }

    pointB() {
        this.mesh.updateWorldMatrix();
        let point = new THREE.Vector3(...this.geometry.attributes.position.array.slice(3, 6));
        return this.mesh.localToWorld(point);
    }

    pointC() {
        this.mesh.updateWorldMatrix();
        let point = new THREE.Vector3(...this.geometry.attributes.position.array.slice(6, 9));
        return this.mesh.localToWorld(point);
    }

    pointD() {
        this.mesh.updateWorldMatrix();
        let point = new THREE.Vector3(...this.geometry.attributes.position.array.slice(9, 12));
        return this.mesh.localToWorld(point);
    }

    lineA() {
        return new THREE.Line3(this.pointA(), this.pointC());
    }

    lineB() {
        return new THREE.Line3(this.pointB(), this.pointD());
    }

    lineC() {
        return new THREE.Line3(this.pointC(), this.pointD());
    }

    lineD() {
        return new THREE.Line3(this.pointA(), this.pointB());
    }

    disposeIntersections() {
        let uuids = new Set();
        for (let each of this.intersections) {
            uuids.add(each.uuid);
        }
        let remove = [];
        for (let c of this.object.children){
            if (uuids.has(c.uuid)) {
                remove.push(c);
            }
        }

        for (let c of remove) {
            if (c.parent !== null ) {
                c.parent.remove(c);
                c.material.dispose();
                c.geometry.dispose();
            }
        }
        this.intersections = [];
    }

    intersectPlane(other) {
        let lines = [this.lineA(), this.lineB(), this.lineC(), this.lineD()];
        let line = null;
        let intersect = [];
        for (let l of lines) {
            let inter = new THREE.Vector3();
            inter = other.plane.intersectLine(l, inter);
            if (inter !== undefined) {
                intersect.push(inter);
            }
            if (intersect.length == 2) {
                break;
            }
        }
        let boundingBox = new THREE.Box3(new THREE.Vector3(0, 2, 0), new THREE.Vector3(0,-2, 0));
        console.log(boundingBox.clampPoint(new THREE.Vector3(10, 10, 10)));
        // let boundedIntersections = [];
        // for (let i of intersect) {
        //     let bounded = new THREE.Vector3();
        //     boundedIntersections.push(boundingBox.clampPoint(i.multiplyScalar(100), bounded));
        // }
        // if (intersect.length == 2) {
        //     // Expand intersection to this plane's boundaries
        //     let localLines = [this.lineA(), this.lineB(), this.lineC(), this.lineD()];
        //     let tempLine = new THREE.Line3(...intersect);
        //     for (let l of lines) {
        //         let inter = new THREE.Vector3();
        //         inter = this.plane.intersectLine(l, inter);
        //         if (inter !== undefined) {
        //             intersect.push(inter);
        //         }
        //         if (intersect.length == 2) {
        //             break;
        //         }
        //     }
        // }
        if (intersect.length == 2) {
            let material = new MeshLineMaterial( {color: other.color,
                                                  lineWidth: .03,
                                                  transparent: true,
                                                  opacity:.8
                                                  } );
            material.renderOrder = 3;
            // material.depthTest = false;
            // material.depthWrite = false;
            let meshline = new MeshLine();
            meshline.setPoints(intersect);
            let geometry = meshline.geometry;
            let b = new THREE.Mesh( geometry, material );
            this.intersections.push(b);
            this.object.add(b);
        }
        // for (let l of lines) {
        //     let material = new MeshLineMaterial( {color: other.color,
        //                                           lineWidth: .02,
        //                                           depthTest: false,
        //                                           transparent: true,
        //                                           opacity: .8,
        //                                           } );
        //     let meshline = new MeshLine();
        //     meshline.setPoints([l.start, l.end]);
        //     let geometry = meshline.geometry;
        //     let b = new THREE.Mesh( geometry, material );
        //     this.object.add(b);
        // }
        // this.object.add(line);
        // return line
    }

}


class BraidArrangement {
    constructor (opacity, width, height) {
        this.op = opacity ?? 1.;
        this.width = width ?? 4;
        this.height = height ?? 4;
        this.hyperplanes = [];
        this. object = new THREE.Group();

        const normals = [[1, -1, 0],
                         [1, 0, -1],
                         [0, -1, -1],
                         [0, 1, -1],
                         [-1, 0,-1],
                         [-1, -1,0]];

        // const normals = [[1, -1, 0],
        //                  [1, 0, -1],
        //                  [0, 1, -1]];


        let colors = new Colors();
        const bcolors = [colors.red,
                         colors.green,
                         colors.blue,
                         colors.purple,
                         colors.yellow,
                         colors.teal,
                         colors.darkblue,
                         colors.darkred,
                         colors.darkgreen,
                         colors.white,
                         colors.grey]
        
        for (let i=0; i < normals.length; i++) {
            let plane = new Hyperplane(...normals[i], 0, bcolors[i], this.width, this.height, this.op);
            this.hyperplanes.push(plane);
            this.object.add( plane.mesh );
        }
    }
}


class Simplex extends Polytope {
    constructor (position, dim, scale, colors) {
        super();
        this.position = position;
		this.dim = dim;
        this.scale = scale ?? 1.;
        this.colors = colors ?? 0x555555;
		if ( this.dim == 4 ) {
			this.positions = [[ -1.,  -1.,  -1.],
							  [ -1.,  1.,  1.],
							  [ 1.,  -1.,  1.],
							  [ 1., 1., -1.]]
            ;
			this.faces = [ 0, 2, 3,
			               3, 2, 1,
			               1, 0, 3,
			               2, 0, 1,
			               ];
            this.radius = Math.sqrt(3);
		}
		else if ( this.dim == 3 ) {
			this.positions = [[ -1.,  .5,  .5],
							  [ .5,  -1., .5],
							  [ .5,  .5, -1.]];
			this.faces = [ 1, 2, 0 ];
            this.radius = Math.sqrt(1.5);
		}
		else {
			this.positions = [[ 1.,  0.,  0.],
							  [ 0.,  0., 1.]];
			this.faces = [];
            this.radius = Math.sqrt(2);
		}
        this.vertexPositions = [];
        for ( const v of this.positions ) {
            const [x, y, z] = v;
            let point = new THREE.Vector3(x * this.scale + this.position[0],
                                          y * this.scale + this.position[1],
                                          z * this.scale + this.position[2]);
            this.vertexPositions.push(point);
        }
        this.vertices = this.constructVertices();
        this.polytope = this.constructPolytope();
    }

    constructVertices() {
        this.labels = [];
        let vertices = new THREE.Group();
        let points = new THREE.Group();
        let labels = new THREE.Group();
        const geometry = new THREE.SphereGeometry(.04, 16, 16);
        const material = new THREE.MeshLambertMaterial( { transparent: true, opacity: 0.9 } );
        for (let i = 0; i < this.positions.length; i++) {

            let sphere = new THREE.Mesh(geometry, material);

            sphere.position.copy( this.vertexPositions[i]);

            // Allow each vertex to be colored independently
            sphere.material = sphere.material.clone();
            if ( Array.isArray(this.colors) ) {
                sphere.material.color = new THREE.Color(this.colors[i]);
            }
            else {
                sphere.material.color = new THREE.Color(this.colors);
            }

            points.add( sphere );

            const text = document.createElement( 'div' );
            text.className = 'label';
            text.style.color = '#' + sphere.material.color.getHexString();
            text.textContent = 'Class ' + String.fromCharCode(97 + i);
            this.labels.push(text);

            const label = new CSS2DObject( text );
            let pos = sphere.position.clone();
            label.position.copy( pos );
            labels.add( label );
        }
        vertices.add( points );
        vertices.add( labels );
        return vertices;
    }

    constructPolytope() {

        let positions = [].concat(...this.positions);
        const geometry = new THREE.PolyhedronBufferGeometry( positions, this.faces, this.radius * this.scale, 0 );
        // const material = new THREE.MeshPhysicalMaterial({ transparent: true, opacity: 0.5, reflectivity: 1., side: THREE.FrontSide});
        const material = new THREE.MeshNormalMaterial({flatShading: true, transparent: true, opacity: 0.1});
        let object = new THREE.Mesh(geometry, material);

        const [x, y, z] = this.position;
        object.translateX(x);
        object.translateY(y);
        object.translateZ(z);

        return object;
    }

    constructBarycentric(){
        const group = new THREE.Group();
        const num_points = this.vertexPositions.length;
        this.barycentricRegions = {};
        for ( let perm of MATH.permutations(num_points) ) {

            let color = new THREE.Color(this.colors[perm[0]]);
            let alpha = .33;
            for ( let p of perm.slice(1, 2) ) {
                let other = new THREE.Color(this.colors[p]);
                color.lerp(other, alpha);
            }

            let points = [];
            for (let i = 0; i < num_points; i++) {
                let point = new THREE.Vector3(0, 0, 0);
                for (let idx of perm.slice(0, i + 1) ) {
                    point.add(this.vertexPositions[idx]);
                }
                point.divideScalar(i + 1);
                points.push(point);
            }
            let geometry;
            if ( this.dim == 4 ) {
                geometry = new ConvexGeometry( points );
            }
            else if ( this.dim == 3 ) {
                geometry = new THREE.BufferGeometry();
                geometry.setFromPoints(points);
                geometry.computeVertexNormals();
            }
            else {
                const ps = [];
                for ( const p of points ) {
                    ps.push(p.x, p.y, p.z);
                }
                let line = new MeshLine()
                line.setPoints(ps)
                geometry = line.geometry;
            }
            let material;
            if ( this.dim > 2 ) {
                // material = new THREE.MeshNormalMaterial( { flatShading: true, transparent: true, side: THREE.DoubleSide, opacity: .25 } );
                material = new THREE.MeshLambertMaterial( { color: color,
                                                            transparent: true,
                                                            side: THREE.DoubleSide,
                                                            opacity: .4 } );
            }
            else {
                material = new MeshLineMaterial( { color: color,
                                                   lineWidth: .05,
                                                   depthTest: false,
                                                   transparent: true,
                                                   opacity: .25,
                                                   } );
            }
            const mesh = new THREE.Mesh( geometry, material );

            const key = perm.join(',');
            this.barycentricRegions[key] = mesh;

            group.add(mesh);
        }
        return group;
    }

    project ( probs ) {
        // For each point
        let projections = [];
        for ( let i = 0; i < probs.length; i++ ) {
            let point = probs[i];
            let point_coords = [0., 0., 0.];
            for ( let j = 0; j < point.length; j++ ) {
                let vertex = this.positions[j];
                for ( let k = 0; k < point_coords.length; k++ ) {
                    point_coords[k] += vertex[k] * point[j];
                }
            }
            projections.push(point_coords);
        }
        return projections;
    }

    plot_project ( probs ) {
        let proj = this.project( probs );
        const geometry = new THREE.SphereGeometry(.04, 16, 16);
        const material = new THREE.MeshLambertMaterial( { color: 0x55cc55, transparent: true, vertexColors: true } );
        let points = new THREE.Group();
        for (let i = 0; i < proj.length; i++) {

            const [x, y, z] = proj[i];
            let sphere = new THREE.Mesh(geometry, material);

            sphere.position.x = x;
            sphere.position.y = y;
            sphere.position.z = z;

            // Allow each vertex to be colored independently
            sphere.material = sphere.material.clone();
            const argm = MATH.argmax(probs[i]);
            sphere.material.color = new THREE.Color(this.colors[argm]);

            points.add( sphere );
        }
        return points
    }

    remove () {
        if ( this.vertices.parent !== null ) {
            const [points, labels] = this.vertices.children;
            for ( const c of labels.children.slice() ) {
                labels.remove( c );
            }
            this.vertices.parent.remove(this.vertices);
        }
        if ( this.polytope.parent !== null ) {
            this.polytope.parent.remove(this.polytope);
        }
        if ( this.barycentricRegions !== undefined ) {
            for ( let region of Object.values(this.barycentricRegions) ) {
                if ( region.parent !== null ) {
                    region.parent.remove(region);
                }
            }
        }
    }
}

export { Permutohedron, Simplex, BraidArrangement, Hyperplane, WeightVector };
